import { CurrentActionTypes } from "@/shared/atoms/usePosOrderCurrentAction";
import { cn } from "@/shared/utils/cn";
import { DeleteIcon } from "../DeleteIcon";
import Icon, { IconsVariant } from "../Icon/Icon";

const DeleteActionButton = ({ onClick }: { onClick: () => void }) => (
  <button
    type="button"
    className="absolute right-1.5 top-1.5 z-20 cursor-pointer rounded-full transition-all hover:scale-105 shadow-[0_2px_6px_rgba(0,0,0,0.15)]"
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
  >
    <DeleteIcon size="xLarge" />
  </button>
);

export interface PosMenuAction {
  name: CurrentActionTypes;
  label: string | React.ReactNode;
  set: (value?: any) => void;
  instantSet?: boolean;
  deleteAction?: () => void;
  hasDeleteAction?: boolean;
  disabled: boolean;
  filtered: boolean;
  iconVariant: IconsVariant;
  variant?: "primary" | "secondary";
}

interface PosMenuBottomActionButtonProps {
  action: PosMenuAction;
  isSelected: boolean;
  onClick: () => void;
}

const PosMenuBottomActionButton = ({
  action,
  isSelected,
  onClick
}: PosMenuBottomActionButtonProps) => {
  const disabled = action.disabled || action.filtered;
  const icon = action.iconVariant;
  const isEmptyTheCartAction = action.name === "emptyTheCart";

  if (action.variant === "primary") {
    return (
      <div className="relative h-full min-h-0 w-full overflow-visible">
        {action.hasDeleteAction && action.deleteAction && (
          <DeleteActionButton onClick={action.deleteAction} />
        )}
        <div
          onClick={disabled ? undefined : onClick}
          className={cn(
            "flex h-full w-full bg-primary cursor-pointer flex-col items-center justify-center gap-2 rounded-lg px-2.5 py-3.5 text-white",
            disabled && "cursor-not-allowed"
          )}
        >
          <Icon variants={icon} className="h-10 w-13 shrink-0 fill-white" />
          <span className="w-full text-center text-3xl text-white font-medium leading-snug line-clamp-2">
            {action.label}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-full min-h-0 w-full overflow-visible">
      {action.hasDeleteAction && action.deleteAction && (
        <DeleteActionButton onClick={action.deleteAction} />
      )}
      <div
        className={cn(
          "relative h-full min-h-0 w-full",
          disabled ? "opacity-50" : "opacity-100"
        )}
      >
        <div
          onClick={disabled ? undefined : onClick}
          className={cn(
            "flex h-full cursor-pointer flex-col rounded-xl px-2 py-4 bg-white",
            disabled && "pointer-events-none",
            isSelected ? "border-2 border-primary" : "border border-border",
            isEmptyTheCartAction && "text-error"
          )}
        >
          <div className="flex h-14 pt-1 shrink-0 items-center justify-center">
            {icon && (
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-grey shadow-md">
                <Icon
                  variants={icon}
                  className={cn(
                    "h-8 w-8 shrink-0 fill-primary",
                    isEmptyTheCartAction && "fill-error"
                  )}
                />
              </div>
            )}
          </div>

          <div className="flex flex-1 items-center justify-center pt-3">
            <span
              className={cn(
                "line-clamp-2 w-full text-center text-text-tertiary font-bold text-2xl leading-[1.2]",
                isEmptyTheCartAction && "text-error"
              )}
            >
              {action.label}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PosMenuBottomActionButton;
